import {
  getCookie,
  deleteSessionCookies,
  getMypageLoginUrl,
  showErrorCondition,
  showErrorMessage,
} from "@/utils";
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import Layout from "@/common/layout/index.vue";
import store, { AccountType } from "@/store";
import staticRoutes from "./staticRoute";
import services from "@/api/services";

// 環境変数から真偽値を取得
const isMypageLoginEnabled =
  process.env.VUE_APP_IS_MYPAGE_LOGIN_ENABLED === "true";

// セッションID用の変数
const sessionIDCookieName =
  process.env.VUE_APP_SESSION_ID_COOKIE_NAME ?? "session_id";

// アプリ起動時フラグ（アプリを閉じている間にユーザー情報が変更されている可能性があるため、起動時は必ず取得）
let isInitialLoad = true;

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: {
      name: "home",
    },
    component: Layout,
    children: [
      // home
      {
        path: "/",
        name: "home",
        component: () => import("../views/HomeView.vue"),
        meta: {
          layoutType: "noAside",
        },
      },
      // static page
      ...staticRoutes,
      // application
      {
        path: "/applications",
        name: "applications",
        component: () =>
          import("../views/manager/application/ApplicationListView.vue"),
        meta: {
          auth: true,
          layoutType: "all",
          breadcrumb: [
            {
              name: "管理ツール",
            },
            {
              name: "アプリケーション一覧",
              to: "/applications",
            },
          ],
          category: "applications",
        },
      },
      {
        path: "/applications/apikey-list",
        name: "applicationApiList",
        component: () =>
          import("../views/manager/application/api/ApiListView.vue"),
        meta: {
          auth: true,
          layoutType: "all",
          breadcrumb: [
            {
              name: "管理ツール",
            },
            {
              name: "アプリケーション一覧",
              to: "/applications",
            },
            {
              name: "APIキー認証アプリケーション一覧",
              to: "/applications/apikey-list",
            },
          ],
          category: "api",
        },
      },
      {
        path: "/applications/apikey-detail/:id",
        name: "applicationApiEdit",
        component: () =>
          import("../views/manager/application/api/edit/index.vue"),
        meta: {
          auth: true,
          breadcrumb: [
            {
              name: "管理ツール",
            },
            {
              name: "アプリケーション一覧",
              to: "/applications",
            },
            {
              name: "APIキー認証",
              to: "/applications/apikey-list",
            },
            {
              name: "APIキー認証アプリケーション設定",
            },
          ],
          layoutType: "all",
          category: "api",
          name: "設定",
        },
      },
      {
        path: "/applications/apikey-create",
        name: "applicationApiAdd",
        component: () =>
          import("../views/manager/application/api/ApiAddView.vue"),
        meta: {
          auth: true,
          layoutType: "all",
          breadcrumb: [
            {
              name: "管理ツール",
            },
            {
              name: "アプリケーション一覧",
              to: "/applications",
            },
            {
              name: "APIキー認証",
              to: "/applications/apikey-list",
            },
            {
              name: "APIキー認証アプリケーション新規作成",
              to: "/applications/apikey-create",
            },
          ],
          category: "api",
          name: "新規作成",
        },
      },
      {
        path: "/applications/oauth-list",
        name: "applicationOAuthList",
        component: () =>
          import("../views/manager/application/oAuth/OAuthListView.vue"),
        meta: {
          auth: true,
          breadcrumb: [
            {
              name: "管理ツール",
            },
            {
              name: "アプリケーション一覧",
              to: "/applications",
            },
            {
              name: "OAuth2.0認証アプリケーション一覧",
            },
          ],
          layoutType: "all",
          category: "oauth",
        },
      },
      {
        path: "/applications/oauth-detail/:id",
        name: "applicationOAuthEdit",
        component: () =>
          import("../views/manager/application/oAuth/edit/index.vue"),
        meta: {
          auth: true,
          layoutType: "all",
          category: "oauth",
          breadcrumb: [
            {
              name: "管理ツール",
            },
            {
              name: "アプリケーション一覧",
              to: "/applications",
            },
            {
              name: "OAuth2.0認証",
              to: "/applications/oauth-list",
            },
            {
              name: "OAuth2.0認証アプリケーション設定",
            },
          ],
          name: "設定",
        },
      },
      {
        path: "/applications/oauth-create",
        name: "application",
        component: () =>
          import("../views/manager/application/oAuth/OAuthAddView.vue"),
        meta: {
          auth: true,
          layoutType: "all",
          category: "oauth",
          breadcrumb: [
            {
              name: "管理ツール",
            },
            {
              name: "アプリケーション一覧",
              to: "/applications",
            },
            {
              name: "OAuth2.0認証",
              to: "/applications/oauth-list",
            },
            {
              name: "OAuth2.0認証アプリケーション新規作成",
            },
          ],
          name: "新規作成",
        },
      },
      {
        path: "/applications/apikey-accessright/:id",
        name: "accessManage",
        component: () => import("../views/manager/access/index.vue"),
        meta: {
          auth: true,
          breadcrumb: [
            {
              name: "管理ツール",
            },
            {
              name: "アプリケーション一覧",
              to: "/applications",
            },
            {
              name: "APIキー認証",
              to: "/applications/apikey-list",
            },
            {
              name: "連携デバイス管理",
            },
          ],
          layoutType: "all",
          category: "api",
          name: "連携デバイス管理",
        },
      },
      // user setting
      {
        path: "login",
        name: "login",
        component: () => import("../views/LoginView.vue"),
        meta: {
          layoutType: "noAsideNoFooter",
        },
      },
      {
        path: "signup",
        name: "signup",
        component: () => import("../views/SignupView.vue"),
        meta: {
          layoutType: "noAsideNoFooter",
        },
      },
      {
        path: "password-reset",
        name: "passwordReset",
        component: () => import("../views/PasswordResetView.vue"),
        meta: {
          layoutType: "noAsideNoFooter",
        },
      },
      {
        path: "password-set",
        name: "passwordSet",
        component: () => import("../views/PasswordSet.vue"),
        meta: {
          auth: true,
          layoutType: "noAsideNoFooter",
        },
      },
      {
        path: "user-setting",
        name: "userInfo",
        component: () => import("../views/UserInfoView.vue"),
        meta: {
          auth: true,
          layoutType: "noAsideNoFooter",
        },
      },
      {
        path: "user-setting-audit",
        name: "userInfoAudit",
        component: () => import("../views/UserInfoView.vue"),
        meta: {
          auth: true,
          layoutType: "noAsideNoFooter",
        },
      },
      {
        path: "/user-setting-edit",
        name: "user-setting-edit",
        component: () => import("../views/manager/userinfo/edit/index.vue"),
        meta: {
          auth: true,
          breadcrumb: [
            {
              name: "管理ツール",
            },
            {
              name: "ディベロッパー情報",
            },
          ],
          layoutType: "all",
          category: "user-setting-edit",
        },
      },
      {
        path: "404",
        name: "404",
        component: () => import("../common/components/error/404.vue"),
        meta: {
          layoutType: "noAside",
        },
      },
      {
        path: "500",
        name: "500",
        component: () => import("../common/components/error/500.vue"),
        meta: {
          layoutType: "noAside",
        },
      },
      {
        path: "403",
        name: "403",
        component: () => import("../common/components/error/403.vue"),
        meta: {
          layoutType: "noAside",
        },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: {
      name: "home",
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from) => {
  const hasSessionCookie = getCookie(sessionIDCookieName);

  const redirectLoginPage = (path?: string) => {
    // session_idがない場合
    if (isMypageLoginEnabled) {
      // Mypageへリダイレクト（遷移先のパスを渡す）
      window.location.href = getMypageLoginUrl(path);
      return false;
    } else {
      // 従来のログイン画面へリダイレクト
      return { path: "/login", query: { redirect: path } };
    }
  };

  if (isMypageLoginEnabled && hasSessionCookie) {
    // セッションCookieがある場合はログイン状態と判定（スコープは考慮しない）
    store.commit("setIsLogin", true);

    // ユーザー情報を取得する条件:
    // - アプリ起動時（isInitialLoadがtrue）- アプリを閉じている間にユーザー情報が変更されている可能性があるため
    // - userinfoがstoreにない場合
    // - セッションIDが変わった場合（別タブでログインした等）
    const sessionIdChanged = hasSessionCookie !== store.state.savedSessionId;
    const shouldFetchUserinfo =
      isInitialLoad || !store.state.userinfo || sessionIdChanged;

    if (shouldFetchUserinfo) {
      try {
        const res = await services.dev.devUserinfoGet();
        store.commit("setInitPassword", undefined);
        store.commit("setUserinfo", res.data);
        store.commit("setLoginUsername", res.data.userinfo?.mail_address);
        store.commit(
          "setIsDedicatedOptionEnabledDeveloper",
          res.data.user_developer?.dedicated_option_enabled ||
            res.data.corporation_developer?.dedicated_option_enabled,
        );
        // ユーザー情報取得時、セッションIDを保存（別タブでのログイン変更検知用）
        store.commit("setSavedSessionId", hasSessionCookie);
        // 初回ロード完了
        isInitialLoad = false;

        const currentAccountType = store.state.currentAccountType;
        if (
          currentAccountType === AccountType.user &&
          res.data.user_developer
        ) {
          store.commit("setDeveloperId", res.data.user_developer.developer_id);
          store.commit(
            "setIsDedicatedOptionEnabledDeveloper",
            res.data.user_developer.dedicated_option_enabled,
          );
        } else if (
          currentAccountType === AccountType.corporation &&
          res.data.corporation_developer
        ) {
          store.commit(
            "setDeveloperId",
            res.data.corporation_developer?.developer_id,
          );
          store.commit(
            "setIsDedicatedOptionEnabledDeveloper",
            res.data.corporation_developer.dedicated_option_enabled,
          );
        } else {
          if (res.data.user_developer) {
            store.commit("setCurrentAccountType", AccountType.user);
            store.commit(
              "setDeveloperId",
              res.data.user_developer.developer_id,
            );
          } else if (res.data.corporation_developer) {
            store.commit("setCurrentAccountType", AccountType.corporation);
            store.commit(
              "setDeveloperId",
              res.data.corporation_developer?.developer_id,
            );
          }
        }

        if (!res.data.user_developer && !res.data.corporation_developer) {
          store.commit("setDeveloperId", undefined);
          if (
            to.meta.auth &&
            to.name !== "userInfo" &&
            to.name !== "userInfoAudit"
          ) {
            return { path: "/user-setting" };
          }
        }
      } catch (reason) {
        // ユーザー情報取得に失敗した場合は、ログアウト状態にする
        store.commit("setUserinfo", undefined);
        store.commit("setDeveloperId", undefined);
        store.commit("setLoginUsername", undefined);
        store.commit("setInitPassword", undefined);
        store.commit("setIsLogin", false);
        store.commit("setSavedSessionId", undefined);

        const error = reason as {
          response?: { status?: number; data?: { message?: string } };
        };

        const errorStatus = error.response?.status;

        // 428エラー（規約未同意）の場合も、ログインページへリダイレクト
        if (errorStatus === 428) {
          return redirectLoginPage(to.fullPath);
        }

        // 要認証ページにアクセスしたい場合
        if (to.meta.auth) {
          // 401エラーの場合は、ログインページへリダイレクト
          if (errorStatus === 401) {
            return redirectLoginPage(to.fullPath);
          }

          // それ以外のエラーの場合はエラーメッセージを出してトップページへ
          const errorMessage = error.response?.data?.message;
          if (errorMessage && showErrorCondition(reason)) {
            showErrorMessage(errorMessage);
          }
          return { path: "/" };
        }
      }
    }
  } else if (!hasSessionCookie) {
    // Cookieがない場合はstoreをリセットしてログアウト状態を設定
    store.commit("setUserinfo", undefined);
    store.commit("setDeveloperId", undefined);
    store.commit("setLoginUsername", undefined);
    store.commit("setInitPassword", undefined);
    store.commit("setIsLogin", false);
    store.commit("setSavedSessionId", undefined);
  }

  // 既存のルーティング判定ロジック
  const initPassword = store.state.initPassword;

  if (initPassword && !hasSessionCookie) {
    store.commit("setInitPassword", undefined);
  }
  if (store.state.userSettingPath === to.path) {
    store.commit("setUserSettingUnload", undefined);
  } else if (to.meta.auth && !hasSessionCookie) {
    return redirectLoginPage(to.fullPath);
  } else if (
    to.meta.auth &&
    hasSessionCookie &&
    !store.state.developerId &&
    !initPassword &&
    !(to.name === "userInfo" || to.name === "userInfoAudit")
  ) {
    return { path: "/user-setting" };
  } else if (
    to.meta.auth &&
    hasSessionCookie &&
    !store.state.developerId &&
    initPassword &&
    to.path !== "/password-set"
  ) {
    // 初期パスワード設定が必要な場合、Cookieを削除してログインページへリダイレクト
    deleteSessionCookies();
    return redirectLoginPage(to.fullPath);
  } else if (to.name === "userInfo") {
    const userinfo = store.state.userinfo;
    if (
      !(
        hasSessionCookie &&
        userinfo &&
        !(userinfo.user_developer || userinfo.corporation_developer)
      )
    ) {
      return { path: "/" };
    }
  } else if (to.name === "userInfoAudit" && from.name !== "userInfo") {
    return { path: "/" };
  }
});

export default router;
